"use client";

import { usePathname } from "next/navigation";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { ChevronRight } from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarHeader,
  SidebarFooter,
} from "@/components/ui/sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { adminNavigation, type NavItem } from "@admin/_lib/navigation";
import { getAvailableDashboards, type DashboardRole } from "@/lib/rbac";
import type { UserInfo } from "@/types/api";

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function filterNavByRoles(items: NavItem[], roles: DashboardRole[]): NavItem[] {
  return items.filter((item) => item.roles.some((r) => roles.includes(r)));
}

export function AdminSidebar() {
  const pathname = usePathname();
  const { data: session } = useSession();
  const user = session?.user as UserInfo | undefined;

  const availableDashboards = user ? getAvailableDashboards(user) : [];
  const activeRoles = availableDashboards.map((d) => d.key);
  const filteredNav = filterNavByRoles(adminNavigation, activeRoles);

  return (
    <Sidebar collapsible="icon">
      {/* ── Logo Header ── */}
      <SidebarHeader className="border-b border-sidebar-border pb-3">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link href="/admin">
                {/* White circle logo — matches Angular's .logo-sub-wrapper { background: #ffffff; border-radius: 50% } */}
                <div
                  className="flex aspect-square size-9 shrink-0 items-center justify-center rounded-full overflow-hidden"
                  style={{
                    background: "#ffffff",
                    boxShadow: "0 0 0 2px hsl(35 100% 50% / 0.5), 0 2px 8px hsl(35 100% 50% / 0.3)",
                  }}
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src="https://all-frontend-assets.s3.amazonaws.com/transcendentpagan/assets/images/logobanner.webp"
                    alt="Divine Infinite Being"
                    width={32}
                    height={32}
                    className="size-8 object-contain"
                  />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold text-foreground">Divine Infinite</span>
                  <span className="truncate text-xs font-medium" style={{ color: "#ffa800" }}>
                    Back Office
                  </span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      {/* ── Navigation ── */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
            Navigation
          </SidebarGroupLabel>
          <SidebarMenu>
            {filteredNav.map((item) =>
              item.children ? (
                <Collapsible
                  key={item.title}
                  asChild
                  defaultOpen={item.children.some((c) => (pathname ?? "").startsWith(c.href ?? ""))}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        tooltip={item.title}
                        className="hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                      >
                        <item.icon className="size-4 shrink-0" aria-hidden="true" />
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto size-3.5 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 text-muted-foreground" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {filterNavByRoles(item.children, activeRoles).map((child) => {
                          const isActive = pathname === child.href || (pathname ?? "").startsWith((child.href ?? "") + "/");
                          return (
                            <SidebarMenuSubItem key={child.title}>
                              <SidebarMenuSubButton
                                asChild
                                isActive={isActive}
                                className={
                                  isActive
                                    ? "font-semibold border-l-2 border-[#ffa800]"
                                    : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground border-l-2 border-transparent"
                                }
                                style={isActive ? {
                                  background: "linear-gradient(to right, rgba(255,168,0,0.18), rgba(255,126,0,0.08))",
                                  color: "#ffa800",
                                } : {}}
                              >
                                <Link href={child.href!}>
                                  <span>{child.title}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          );
                        })}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              ) : (
                <SidebarMenuItem key={item.title}>
                  {(() => {
                    const isActive = pathname === item.href || (pathname ?? "").startsWith((item.href ?? "") + "/");
                    return (
                      <SidebarMenuButton
                        asChild
                        isActive={isActive}
                        tooltip={item.title}
                        className={
                          isActive
                            ? "font-semibold"
                            : "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                        }
                        style={isActive ? {
                          background: "linear-gradient(to right, rgba(255,168,0,0.2), rgba(255,126,0,0.08))",
                          color: "#ffa800",
                          borderLeft: "2px solid #ffa800",
                        } : {}}
                      >
                        <Link href={item.href!}>
                          <item.icon
                            className={`size-4 shrink-0`}
                            aria-hidden="true"
                          />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    );
                  })()}
                </SidebarMenuItem>
              )
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      {/* ── User Footer ── */}
      <SidebarFooter className="border-t border-sidebar-border pt-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              className="hover:bg-sidebar-accent cursor-default pointer-events-none"
            >
              <Avatar className="size-8 shrink-0">
                <AvatarFallback
                  className="text-xs font-semibold"
                  style={{
                    background: "linear-gradient(135deg, rgba(255,168,0,0.2), rgba(255,126,0,0.1))",
                    color: "#ffa800",
                    border: "1px solid rgba(255,168,0,0.35)",
                  }}
                >
                  {user ? getInitials(user.name || user.email) : "?"}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                <span className="truncate font-semibold text-foreground">{user?.name ?? "User"}</span>
                <span className="truncate text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
